import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage, localizedPath, switchPath } from "@/lib/i18n";
import { Logo } from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Start", testid: "nav-link-start" },
  { to: "/projekte", label: "Projekte", testid: "nav-link-projekte" },
  { to: "/fieldnotes", label: "Fieldnotes", testid: "nav-link-fieldnotes" },
  { to: "/ueber-mich", label: "Über mich", testid: "nav-link-ueber-mich" },
  { to: "/kontakt", label: "Kontakt", testid: "nav-link-kontakt" },
];

// Sprach-Schieberegler: führt zur selben Seite in der anderen Sprache,
// die aktive Sprache trägt den orangenen Punkt
const LangToggle = ({ lang, id = "header", className = "" }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
  <div
    className={`flex items-center gap-5 font-mono text-[11px] uppercase tracking-[0.28em] ${className}`}
    data-testid={`lang-toggle-${id}`}
  >
    {["de", "en"].map((code) => (
      <button
        key={code}
        type="button"
        data-testid={`lang-${code}-btn-${id}`}
        onClick={() => navigate(switchPath(pathname, code))}
        className={`relative uppercase transition-colors duration-300 ${
          lang === code ? "text-bone" : "text-smoke hover:text-bone"
        }`}
      >
        {code}
        {lang === code && (
          <motion.span
            layoutId={`lang-dot-${id}`}
            className="absolute -bottom-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-ember"
            transition={{ type: "spring", stiffness: 500, damping: 38 }}
          />
        )}
      </button>
    ))}
  </div>
  );
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { lang, t } = useLanguage();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-obsidian/85 backdrop-blur-md"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-12 lg:px-16">
          <Link
            to={localizedPath("/", lang)}
            data-testid="header-logo-link"
            aria-label={t("Startseite")}
            className="text-bone transition-colors duration-300 hover:text-ember"
          >
            <Logo width={46} />
          </Link>
          <div
            className={`flex items-center py-3 transition-colors duration-500 ${
              scrolled ? "" : "bg-obsidian/70 backdrop-blur-md"
            }`}
            data-testid="header-controls"
          >
            <LangToggle lang={lang} className="px-4 md:px-5" />
            <span className="h-3.5 w-px bg-bone/15" aria-hidden="true" />
            <button
              type="button"
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              className="flex items-center gap-2.5 px-4 font-mono text-[11px] uppercase tracking-[0.28em] text-smoke transition-colors duration-300 hover:text-ember md:px-5"
              aria-label={t("Menü öffnen")}
            >
              {t("Menü")} <Menu size={17} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-obsidian"
            data-testid="nav-mobile-overlay"
          >
            <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-6 md:px-12 lg:px-16">
              <span className="text-bone/30">
                <Logo width={54} />
              </span>
              <button
                type="button"
                data-testid="nav-menu-close"
                onClick={() => setOpen(false)}
                aria-label={t("Menü schließen")}
                className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.28em] text-smoke transition-colors duration-300 hover:text-ember-deep"
              >
                {t("Schließen")} <X size={17} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center gap-1 px-6 md:px-12 lg:px-16">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={localizedPath(l.to, lang)}
                    data-testid={`nav-mobile-${l.testid}`}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-5 py-2 font-serif text-5xl font-light md:py-3 md:text-7xl ${
                        isActive ? "italic text-ember" : "text-bone"
                      }`
                    }
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/60 transition-colors duration-300 group-hover:text-ember-deep">
                      0{i + 1}
                    </span>
                    <span className="transition-colors duration-300 group-hover:text-ember">
                      {t(l.label)}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 pb-10 md:px-12 lg:px-16">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                {t("Hamburg · Buchungen 2026/2027")}
              </p>
              <LangToggle lang={lang} id="overlay" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
